import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import ClientInfoForm from './components/ClientInfoForm'
import ThemeAssessment from './components/ThemeAssessment'
import ProgressStepper from './components/ProgressStepper'
import AssessmentSummary from './components/AssessmentSummary'
import { useAssessment } from './hooks/useAssessment.jsx'
import { useAssessmentProgress, usePersistentUserInfo } from './hooks/useLocalStorage'
import cedecLogo from './assets/Cedec-logo-nosub.png'
import './index.css'

const STEPS = {
  CLIENT_INFO: 'client-info',
  ASSESSMENT: 'assessment',
  RESULTS: 'results',
  SUCCESS: 'success'
}

// Map routes to step names for ProgressStepper
const getStepFromPath = (pathname) => {
  if (pathname === '/') return STEPS.CLIENT_INFO
  if (pathname === '/assessment') return STEPS.ASSESSMENT
  if (pathname === '/results') return STEPS.RESULTS
  if (pathname === '/success') return STEPS.SUCCESS
  return STEPS.CLIENT_INFO
}

function App() {
  const navigate = useNavigate()
  const location = useLocation()
  // 📦 Persistent user info (survives across assessments)
  const {
    persistentUserInfo,
    savePersistentUserInfo,
    clearPersistentUserInfo
  } = usePersistentUserInfo()

  // 📝 Temporary assessment progress (cleared after each assessment)
  const { 
    clientInfo: savedClientInfo, 
    assessmentScores: savedScores,
    assessmentData: savedAssessmentData,
    currentStep: savedStep,
    currentThemeIndex,
    saveClientInfo,
    saveAssessmentScores,
    saveCompleteAssessmentData,
    setCurrentStep: setSavedCurrentStep,
    clearProgress,
    clearAll,
    hasProgress,
    hasCompleteAssessmentData
  } = useAssessmentProgress()
  
  const [clientData, setClientData] = useState(savedClientInfo)
  // Try to load saved assessment data, otherwise null
  const [assessmentData, setAssessmentData] = useState(savedAssessmentData)
  
  const { submitAssessment, sendAssessmentEmail, downloadPDF, isSubmitting, error } = useAssessment()

  // Get current step from URL path
  const currentStep = getStepFromPath(location.pathname)

  // 🛡️ DEFENSIVE: On mount, validate saved step and navigate if needed
  useEffect(() => {
    // If we're on SUCCESS page but don't have complete data, redirect to start
    if (location.pathname === '/success') {
      if (!hasCompleteAssessmentData()) {
        console.warn('⚠️ On SUCCESS page but complete assessmentData not available. Redirecting to start.')
        clearProgress()
        navigate('/', { replace: true })
      } else {
        console.log('✅ Restoring SUCCESS page with saved assessment data')
      }
    }
    // If we have saved progress and we're on home page, maybe restore to saved step
    else if (location.pathname === '/' && savedStep && savedStep !== STEPS.CLIENT_INFO) {
      // User might want to continue from where they left off
      // But we don't automatically redirect - let them choose via the banner
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Update local storage when URL changes
  // BUT: Don't overwrite "assessment" with "client-info" when navigating back to home with progress
  useEffect(() => {
    // If we're on home page but have assessment progress, don't overwrite the saved step
    if (currentStep === STEPS.CLIENT_INFO && savedStep === STEPS.ASSESSMENT && hasProgress()) {
      console.log('🛡️ Preserving assessment step - user has progress')
      return // Don't overwrite
    }
    
    setSavedCurrentStep(currentStep)
  }, [currentStep, setSavedCurrentStep, savedStep, hasProgress])

  // Update page title based on current URL/step
  useEffect(() => {
    const stepTitles = {
      [STEPS.CLIENT_INFO]: 'Informations Client - CEDEC',
      [STEPS.ASSESSMENT]: 'Évaluation en cours - CEDEC',
      [STEPS.RESULTS]: 'Génération du rapport - CEDEC',
      [STEPS.SUCCESS]: 'Rapport envoyé - CEDEC'
    }
    
    document.title = stepTitles[currentStep] || 'CEDEC - Diagnostic Complet d\'Entreprise'
  }, [currentStep])

  const handleClientInfoSubmit = (data, shouldResume = false) => {
    try {
      // Validate client data
      if (!data || !data.email || !data.company) {
        console.error('❌ Invalid client data:', data)
        alert('Erreur: Données client invalides. Veuillez réessayer.')
        return
      }
      
      setClientData(data)
      saveClientInfo(data) // Save to temporary assessment progress
      savePersistentUserInfo(data) // Save to persistent storage (survives across assessments)
      
      // If resuming and we have a saved theme, navigate to that theme
      if (shouldResume && savedStep === STEPS.ASSESSMENT && typeof currentThemeIndex === 'number') {
        const themeIndex = Math.max(0, Math.floor(currentThemeIndex))
        console.log(`✅ Starting fresh assessment with client: ${data.company}`)
        navigate(`/assessment?theme=${themeIndex}`)
      } else {
        // Fresh start - go to theme 0
        console.log(`✅ Starting fresh assessment with client: ${data.company}`)
        navigate('/assessment?theme=0')
      }
    } catch (error) {
      console.error('❌ Error submitting client info:', error)
      alert('Une erreur est survenue. Veuillez réessayer.')
    }
  }
  
  const handleResumeAssessment = () => {
    try {
      // Validate saved data before resuming
      if (!savedClientInfo) {
        console.warn('⚠️ No saved client info, starting fresh')
        navigate('/assessment?theme=0')
        return
      }
      
      if (savedStep !== STEPS.ASSESSMENT) {
        console.warn('⚠️ Not in assessment step, starting fresh')
        navigate('/assessment?theme=0')
        return
      }
      
      // Validate theme index
      const themeIndex = typeof currentThemeIndex === 'number' && currentThemeIndex >= 0 
        ? Math.floor(currentThemeIndex)
        : 0
      
      console.log(`✅ Resuming assessment at theme ${themeIndex}`)
      navigate(`/assessment?theme=${themeIndex}`)
    } catch (error) {
      console.error('❌ Error resuming assessment:', error)
      // Safe fallback
      navigate('/assessment?theme=0')
    }
  }

  const handleAssessmentComplete = async (data) => {
    // 🛡️ Validate assessment data before proceeding
    if (!data || !data.scores || !data.themes) {
      console.error('❌ Invalid assessment data received', data)
      alert('Erreur: Données d\'évaluation invalides. Veuillez réessayer.')
      return
    }

    setAssessmentData(data)
    navigate('/results')
    
    try {
      // ✅ Use the CORRECT method: Generate PDF on frontend + send via email
      // This uses React-PDF instead of the broken backend text generator
      const result = await sendAssessmentEmail(clientData, data)
      console.log('Assessment submitted successfully with React-PDF:', result)
      
      // 💾 Save complete assessment data to localStorage (for page refresh on SUCCESS)
      saveCompleteAssessmentData(data)
      
      // Move to success step
      navigate('/success')
      
    } catch (error) {
      console.error('Submission failed:', error)
      // Stay on results page to show error
    }
  }

  const handleBack = () => {
    // Use browser's native back navigation
    navigate(-1)
  }

  const handleDownloadPDF = async () => {
    try {
      await downloadPDF(clientData, assessmentData)
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error)
    }
  }

  const handleRestart = () => {
    console.log('🔄 Restarting assessment...')
    setClientData(null)
    setAssessmentData(null)
    clearProgress()
    navigate('/', { replace: true })
  }


  // Layout wrapper component to share common UI
  const AppLayout = ({ children }) => (
    <div className="min-h-screen cedec-gradient">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <header className="text-center mb-12 bg-white rounded-2xl shadow-lg p-8 border-b-4 border-cedec-blue">
          <div className="flex flex-col items-center justify-center">
            <img 
              src={cedecLogo} 
              alt="CEDEC" 
              className="h-10 md:h-10 lg:h-12 w-auto mb-6 object-contain transition-all duration-300"
            />
            <h1 className="text-2xl md:text-3xl text-gray-700 font-semibold">
              Score de Résilience
            </h1>
            <p className="text-sm md:text-base text-gray-500 mt-2">
              Évaluation approfondie accompagnée
            </p>
          </div>
        </header>

        {/* Progress Saved Banner - Only show on assessment and results pages, NOT on home page */}
        {hasProgress() && currentStep !== STEPS.SUCCESS && currentStep !== STEPS.CLIENT_INFO && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6 rounded-r-lg">
            <div className="flex items-center justify-between">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    <span className="font-medium">Votre progression est automatiquement sauvegardée</span>
                    {savedClientInfo && (
                      <span className="block mt-1">
                        Évaluation pour <strong>{savedClientInfo.company}</strong> en cours...
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRestart}
                  className="text-sm bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-md transition-colors"
                >
                  🗑️ Recommencer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Stepper */}
        <ProgressStepper currentStep={currentStep} />

        {/* Main Content */}
        <main className="mt-8">
          {children}
        </main>
      </div>
    </div>
  )

  return (
    <Routes>
      <Route path="/" element={
        <AppLayout>
          <ClientInfoForm 
            onSubmit={handleClientInfoSubmit}
            onResume={handleResumeAssessment}
            defaultValues={savedClientInfo || persistentUserInfo}
            hasProgress={hasProgress()}
            savedStep={savedStep} // From localStorage, not URL
            savedThemeIndex={currentThemeIndex}
            hasScores={!!savedScores && Object.keys(savedScores).length > 0}
          />
        </AppLayout>
      } />
      
      <Route path="/assessment" element={
        <AppLayout>
          <ThemeAssessment 
            onComplete={handleAssessmentComplete}
            onBack={handleBack}
            clientData={clientData}
          />
        </AppLayout>
      } />
      
      <Route path="/results" element={
        <AppLayout>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-cedec-blue mb-6">
              Génération du rapport en cours...
            </h2>
            
            {isSubmitting && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cedec-blue mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    📄 Génération du rapport PDF...<br />
                    📧 Envoi par email...
                  </p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 font-semibold mb-2">Erreur</h3>
                <p className="text-red-700">{error}</p>
                <button
                  onClick={handleBack}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Retour à l'évaluation
                </button>
              </div>
            )}
          </div>
        </AppLayout>
      } />
      
      <Route path="/success" element={
        <AppLayout>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <AssessmentSummary
              clientData={clientData}
              assessmentData={assessmentData}
              onDownloadPDF={handleDownloadPDF}
              isDownloading={isSubmitting}
            />
            
            <div className="mt-8 text-center border-t pt-6">
              <button
                onClick={handleRestart}
                className="text-cedec-blue hover:text-blue-700 font-medium inline-flex items-center gap-2"
              >
                ← Nouvelle évaluation
              </button>
            </div>
          </div>
        </AppLayout>
      } />
    </Routes>
  )
}

export default App